/**
 * Goal celebration VFX — confetti over the bowl, and fireworks at night.
 * (Owner, 2026-08-09: "when goal happens show confetti from above the stadium,
 * in night mode crackers".)
 *
 * ── Shape ───────────────────────────────────────────────────────────────────
 * One `Points` cloud for confetti and one for firework sparks, both allocated
 * ONCE at init and reused. Nothing is created or destroyed per goal: a goal is
 * a moment of peak visual load — replay camera, celebration anims, banner,
 * crowd — and allocating a few thousand particles right then is exactly how you
 * earn a hitch on the one frame nobody will forgive it on.
 *
 * ── Why Points and not instanced quads ─────────────────────────────────────
 * Confetti seen from a camera on the pitch is 20-60 m away and a few
 * centimetres across. At that distance a rotating textured quad and a round
 * sprite are the same handful of pixels, and the sprite costs one vertex
 * instead of four plus a per-instance matrix. Fidelity nobody can resolve is
 * just heat.
 *
 * ── Determinism ─────────────────────────────────────────────────────────────
 * This is presentation only and lives entirely outside `/sim`. It uses its own
 * cheap integer hash rather than `Math.random()` — not because the netcode
 * cares (it cannot see this), but because a replayed match should look the same
 * twice, and because reaching for Math.random in this codebase is a habit worth
 * not forming (README §15.3).
 */

import {
  AdditiveBlending, BufferAttribute, BufferGeometry, Color, NormalBlending,
  Points, PointsMaterial, type Scene,
} from 'three';
import type { RenderContext, RenderState, RenderSubsystem } from './context.js';

/** Confetti particles. Enough to read as a shower without a fill-rate spike. */
const CONFETTI_COUNT = 1400;
/** Firework sparks, shared across all live shells. */
const SPARK_COUNT = 900;

/** How long a confetti burst lives, seconds. Outlasts the replay deliberately. */
const CONFETTI_LIFE_S = 9;
const SPARK_LIFE_S = 1.9;

/*
 * Confetti fills a COLUMN from just above head height to the top of the bowl,
 * rather than being released from the roof alone.
 *
 * Released purely from 34 m it is invisible: paper falls at ~2 m/s, so it needs
 * fifteen seconds to reach a camera looking at chest height, and the whole
 * celebration is over in about five. Verified by capture — the first version
 * spawned a perfectly good shower that the player never saw a single fleck of.
 * Seeding the whole column means there is paper in frame on the first frame AND
 * a continuing fall from above, which is what a stadium actually looks like.
 */
const CONFETTI_SPAWN_Y = 2.5;
const CONFETTI_SPAWN_SPREAD_Y = 34;
/** Terminal-ish fall speed. Paper does not plummet; it dawdles. */
const CONFETTI_FALL_MIN = 2.2;
const CONFETTI_FALL_MAX = 4.6;
/** Lateral drift, so it flutters across the pitch instead of dropping in lines. */
const CONFETTI_DRIFT = 2.2;
/** Flutter — the horizontal wobble that makes paper read as paper. */
const CONFETTI_FLUTTER_HZ = 1.7;
const CONFETTI_FLUTTER_M = 0.9;

/*
 * Shells burst just above the stand roofline — NOT high in the sky.
 *
 * The replay camera is a goal-facing shot at 6.5 m looking at chest height, so
 * anything above ~30 m is outside the frustum entirely. Fireworks nobody can
 * see are just heat, and the first attempt put them at 42-58 m.
 */
const SHELL_MIN_Y = 17;
const SHELL_MAX_Y = 30;
const SPARK_SPEED = 14;
const SPARK_GRAVITY = 9.0;
/** Sparks per shell; SPARK_COUNT / this is how many shells can be in the air. */
const SPARKS_PER_SHELL = 150;

/** Cheap deterministic hash → [0,1). No Math.random anywhere in this file. */
function rand01(seed: number): number {
  let h = seed >>> 0;
  h ^= h >>> 16;
  h = Math.imul(h, 0x21f0aaad) >>> 0;
  h ^= h >>> 15;
  h = Math.imul(h, 0x735a2d97) >>> 0;
  h ^= h >>> 15;
  return (h >>> 8) / 0x01000000;
}

export interface CelebrationHandle {
  /**
   * Fire a celebration.
   *
   * `teamColorA/B` are the SCORING side's kit colours, so the confetti is the
   * right colour for whoever just scored — a stadium that fires the same
   * generic colours for both teams looks like a screensaver.
   * `night` adds the fireworks; by day they would be invisible anyway.
   */
  burst(
    x: number,
    z: number,
    kitPrimary: readonly [number, number, number],
    kitSecondary: readonly [number, number, number],
    night: boolean,
  ): void;
  /** Stop everything immediately (quit to menu, new match). */
  clear(): void;
}

interface CelebrationSubsystem extends RenderSubsystem {
  readonly handle: CelebrationHandle;
}

export function createCelebrationVfx(): CelebrationSubsystem {
  // ── Confetti buffers ─────────────────────────────────────────────────────
  const cPos = new Float32Array(CONFETTI_COUNT * 3);
  const cCol = new Float32Array(CONFETTI_COUNT * 3);
  const cVel = new Float32Array(CONFETTI_COUNT * 3);
  /** Seconds of life remaining; <= 0 is a dead (invisible) particle. */
  const cLife = new Float32Array(CONFETTI_COUNT);
  const cPhase = new Float32Array(CONFETTI_COUNT);

  // ── Spark buffers ────────────────────────────────────────────────────────
  const sPos = new Float32Array(SPARK_COUNT * 3);
  const sCol = new Float32Array(SPARK_COUNT * 3);
  const sVel = new Float32Array(SPARK_COUNT * 3);
  const sLife = new Float32Array(SPARK_COUNT);

  let confetti: Points | null = null;
  let sparks: Points | null = null;
  let scene: Scene | null = null;
  let seed = 0x9e37;
  let nextSpark = 0;
  /** Queued shells, so a burst goes up in volleys instead of one flat pop. */
  const pending: { atS: number; x: number; z: number; hue: number }[] = [];
  let clock = 0;

  const tmp = new Color();

  const buildPoints = (
    posArr: Float32Array,
    colArr: Float32Array,
    size: number,
    additive: boolean,
  ): Points => {
    const g = new BufferGeometry();
    g.setAttribute('position', new BufferAttribute(posArr, 3));
    g.setAttribute('color', new BufferAttribute(colArr, 3));
    const m = new PointsMaterial({
      size,
      vertexColors: true,
      transparent: true,
      depthWrite: false,
      sizeAttenuation: true,
      blending: additive ? AdditiveBlending : NormalBlending,
    });
    const p = new Points(g, m);
    // Confetti and sparks span the whole bowl and are spawned off-camera; the
    // default sphere bound is computed once from a zeroed buffer and would cull
    // the entire cloud the moment the camera looks away from the origin.
    p.frustumCulled = false;
    p.renderOrder = 10;
    return p;
  };

  const emitShell = (x: number, z: number, hue: number): void => {
    const cy = SHELL_MIN_Y + rand01(seed++) * (SHELL_MAX_Y - SHELL_MIN_Y);
    // Cluster around the goal end that is being filmed, not the whole bowl —
    // a shell behind the camera is indistinguishable from no shell at all.
    const cx = x * 0.8 + (rand01(seed++) - 0.5) * 34;
    const cz = z + (rand01(seed++) - 0.5) * 44;
    tmp.setHSL(hue, 0.85, 0.6);
    for (let k = 0; k < SPARKS_PER_SHELL; k++) {
      const i = nextSpark % SPARK_COUNT;
      nextSpark++;
      // Even-ish sphere: acos gives uniform latitude, which stops the burst
      // bunching at the poles the way a naive uniform theta/phi does.
      const u = rand01(seed++) * 2 - 1;
      const phi = rand01(seed++) * Math.PI * 2;
      const r = Math.sqrt(Math.max(0, 1 - u * u));
      const spd = SPARK_SPEED * (0.55 + rand01(seed++) * 0.6);
      sPos[i * 3] = cx;
      sPos[i * 3 + 1] = cy;
      sPos[i * 3 + 2] = cz;
      sVel[i * 3] = r * Math.cos(phi) * spd;
      sVel[i * 3 + 1] = u * spd;
      sVel[i * 3 + 2] = r * Math.sin(phi) * spd;
      sCol[i * 3] = tmp.r;
      sCol[i * 3 + 1] = tmp.g;
      sCol[i * 3 + 2] = tmp.b;
      sLife[i] = SPARK_LIFE_S * (0.7 + rand01(seed++) * 0.5);
    }
  };

  const handle: CelebrationHandle = {
    burst(x, z, kitPrimary, kitSecondary, night): void {
      // kit-colors.ts hands out LINEAR triples, and three's working colour
      // space is linear — so these go in as-is. Wrapping them in `new
      // Color(hex)` would apply an sRGB decode a second time and wash the
      // confetti out to pastel.
      const a = new Color().setRGB(kitPrimary[0], kitPrimary[1], kitPrimary[2]);
      const b = new Color().setRGB(kitSecondary[0], kitSecondary[1], kitSecondary[2]);
      const white = new Color().setRGB(1, 1, 1);

      for (let i = 0; i < CONFETTI_COUNT; i++) {
        // Spread across the whole bowl, not just over the goal — confetti is
        // released by the stands, and the stands are everywhere.
        // Weighted toward the scoring end (where the camera is), with a long
        // tail across the rest of the bowl so it does not look like a local
        // effect bolted onto one goalmouth.
        const nearEnd = rand01(seed++) < 0.65;
        cPos[i * 3] = nearEnd
          ? x * 0.75 + (rand01(seed++) - 0.5) * 50
          : (rand01(seed++) - 0.5) * 130;
        cPos[i * 3 + 1] = CONFETTI_SPAWN_Y + rand01(seed++) * CONFETTI_SPAWN_SPREAD_Y;
        cPos[i * 3 + 2] = (rand01(seed++) - 0.5) * (nearEnd ? 60 : 95);

        cVel[i * 3] = (rand01(seed++) - 0.5) * CONFETTI_DRIFT;
        cVel[i * 3 + 1] = -(CONFETTI_FALL_MIN + rand01(seed++) * (CONFETTI_FALL_MAX - CONFETTI_FALL_MIN));
        cVel[i * 3 + 2] = (rand01(seed++) - 0.5) * CONFETTI_DRIFT;

        // Mostly the scorer's two kit colours, with a little white for contrast
        // against a dark night sky.
        const pick = rand01(seed++);
        const c = pick < 0.45 ? a : pick < 0.9 ? b : white;
        cCol[i * 3] = c.r;
        cCol[i * 3 + 1] = c.g;
        cCol[i * 3 + 2] = c.b;

        cLife[i] = CONFETTI_LIFE_S * (0.7 + rand01(seed++) * 0.5);
        cPhase[i] = rand01(seed++) * Math.PI * 2;
      }

      if (night) {
        // A volley, not a single pop: shells staggered over a couple of seconds
        // is what reads as a celebration rather than a glitch.
        pending.length = 0;
        for (let s = 0; s < 6; s++) {
          pending.push({
            atS: clock + s * 0.42 + rand01(seed++) * 0.25,
            x,
            z,
            hue: rand01(seed++),
          });
        }
      }
    },

    clear(): void {
      cLife.fill(0);
      sLife.fill(0);
      pending.length = 0;
    },
  };

  return {
    name: 'celebration-vfx',
    handle,

    init(ctx: RenderContext): void {
      scene = ctx.scene;
      confetti = buildPoints(cPos, cCol, 0.55, false);
      sparks = buildPoints(sPos, sCol, 0.75, true);
      // Start fully dead so nothing shows before the first goal.
      cLife.fill(0);
      sLife.fill(0);
      cPos.fill(0);
      sPos.fill(0);
      scene.add(confetti);
      scene.add(sparks);
    },

    update(state: RenderState): void {
      const dt = Math.min(state.dt, 0.1); // a tab-restore spike must not teleport particles
      clock += dt;

      // Release any queued firework shells whose time has come.
      for (let i = pending.length - 1; i >= 0; i--) {
        const s = pending[i]!;
        if (clock >= s.atS) {
          emitShell(s.x, s.z, s.hue);
          pending.splice(i, 1);
        }
      }

      let anyConfetti = false;
      for (let i = 0; i < CONFETTI_COUNT; i++) {
        if (cLife[i]! <= 0) continue;
        anyConfetti = true;
        cLife[i] = cLife[i]! - dt;
        if (cLife[i]! <= 0) {
          // Park dead particles far below the pitch rather than leaving them at
          // their last position, where they would freeze mid-air until reused.
          cPos[i * 3 + 1] = -1000;
          continue;
        }
        cPhase[i] = cPhase[i]! + dt * CONFETTI_FLUTTER_HZ * Math.PI * 2;
        cPos[i * 3] = cPos[i * 3]! + (cVel[i * 3]! + Math.cos(cPhase[i]!) * CONFETTI_FLUTTER_M) * dt;
        cPos[i * 3 + 1] = cPos[i * 3 + 1]! + cVel[i * 3 + 1]! * dt;
        cPos[i * 3 + 2] = cPos[i * 3 + 2]! + (cVel[i * 3 + 2]! + Math.sin(cPhase[i]!) * CONFETTI_FLUTTER_M) * dt;
        // Settle on the deck instead of sinking through it.
        if (cPos[i * 3 + 1]! < 0.02) {
          cPos[i * 3 + 1] = 0.02;
          cVel[i * 3 + 1] = 0;
          cVel[i * 3] = 0;
          cVel[i * 3 + 2] = 0;
        }
      }

      let anySpark = false;
      for (let i = 0; i < SPARK_COUNT; i++) {
        if (sLife[i]! <= 0) continue;
        anySpark = true;
        sLife[i] = sLife[i]! - dt;
        if (sLife[i]! <= 0) {
          sPos[i * 3 + 1] = -1000;
          continue;
        }
        sVel[i * 3 + 1] = sVel[i * 3 + 1]! - SPARK_GRAVITY * dt;
        sPos[i * 3] = sPos[i * 3]! + sVel[i * 3]! * dt;
        sPos[i * 3 + 1] = sPos[i * 3 + 1]! + sVel[i * 3 + 1]! * dt;
        sPos[i * 3 + 2] = sPos[i * 3 + 2]! + sVel[i * 3 + 2]! * dt;
        // Fade by dimming the colour — cheaper than a per-point alpha attribute
        // and indistinguishable at this size against the sky.
        const f = Math.max(0, Math.min(1, sLife[i]! / SPARK_LIFE_S));
        sCol[i * 3] = sCol[i * 3]! * (0.965 + f * 0.035);
        sCol[i * 3 + 1] = sCol[i * 3 + 1]! * (0.955 + f * 0.045);
        sCol[i * 3 + 2] = sCol[i * 3 + 2]! * (0.95 + f * 0.05);
      }

      // Only pay for the upload when something actually moved.
      if (confetti && anyConfetti) {
        (confetti.geometry.getAttribute('position') as BufferAttribute).needsUpdate = true;
      }
      if (sparks && anySpark) {
        (sparks.geometry.getAttribute('position') as BufferAttribute).needsUpdate = true;
        (sparks.geometry.getAttribute('color') as BufferAttribute).needsUpdate = true;
      }
      if (confetti) confetti.visible = anyConfetti;
      if (sparks) sparks.visible = anySpark;
    },

    dispose(): void {
      for (const p of [confetti, sparks]) {
        if (!p) continue;
        scene?.remove(p);
        p.geometry.dispose();
        (p.material as PointsMaterial).dispose();
      }
      confetti = null;
      sparks = null;
      scene = null;
    },
  };
}
